/**
 * The client dashboard's API (BIBLE §19.5) — its own slice, because it is its own API:
 * `/c/{slug}/api/...`, authenticated by an HttpOnly session cookie the browser carries by
 * itself, not by the operator key the console's `baseQuery` adds.
 *
 * With one exception: when the operator opens a dashboard from the console, the operator
 * key this tab already holds rides along and opens it read-only, and the dashboard says
 * so. A key the engine refuses is dropped, as the console would drop it.
 *
 * Any 401 re-asks `me`, which then answers 401 too and puts the login page up — a session
 * that ends mid-visit lands on the login page rather than on a broken screen.
 */

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { loggedOut, persistKey } from '~/app/authSlice'
import type { AuthState } from '~/app/authSlice'
import { OPERATOR_KEY_HEADER } from './baseQuery'
import type { components } from './schema'

type S = components['schemas']

export type PortalMe = S['PortalMe']
export type PortalOverview = S['GetPortalOverviewResult']
export type PortalHeadline = S['Headline']
export type PortalChannelHeadline = S['ChannelHeadline']
export type PortalCampaign = S['PortalCampaign']
export type PortalFunnel = S['PortalFunnel']
export type PortalCampaignsResult = S['ListPortalCampaignsResult']
export type PortalCampaignResult = S['GetPortalCampaignResult']
export type PortalDay = S['PortalDay']
export type PortalRepliesResult = S['ListPortalRepliesResult']
export type PortalReplyRow = S['PortalReplyRow']
export type PortalReplyResult = S['GetPortalReplyResult']
export type PortalMessage = S['PortalMessage']

const ORIGIN = globalThis.location?.origin ?? 'http://localhost'

/** Replies per page on the dashboard. */
export const REPLIES_PAGE = 25

/** `/c/{slug}/api` — where every dashboard call goes. */
export function portalBase(slug: string): string {
  return `/c/${encodeURIComponent(slug)}/api`
}

const rawPortalQuery = fetchBaseQuery({
  baseUrl: ORIGIN,
  // The session cookie is same-origin and HttpOnly: the browser sends it, this code never
  // sees it.
  credentials: 'same-origin',
  prepareHeaders: (headers, { getState }) => {
    const { key } = (getState() as { auth: AuthState }).auth
    if (key) headers.set(OPERATOR_KEY_HEADER, key)
    return headers
  },
})

const portalBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const result = await rawPortalQuery(args, api, extraOptions)
  if (result.error?.status === 401) {
    const { key } = (api.getState() as { auth: AuthState }).auth
    if (key) {
      persistKey(null)
      api.dispatch(loggedOut())
    }
    if (api.endpoint !== 'portalMe') api.dispatch(portalApi.util.invalidateTags(['Me']))
  }
  return result
}

export const portalApi = createApi({
  reducerPath: 'portalApi',
  baseQuery: portalBaseQuery,
  tagTypes: ['Me', 'Data'],
  keepUnusedDataFor: 300,
  endpoints: (build) => ({
    portalMe: build.query<PortalMe, string>({
      query: (slug) => `${portalBase(slug)}/me`,
      providesTags: ['Me'],
    }),
    portalLogIn: build.mutation<PortalMe, { slug: string; email: string; password: string }>({
      query: ({ slug, ...body }) => ({ url: `${portalBase(slug)}/login`, method: 'POST', body }),
      invalidatesTags: ['Me', 'Data'],
    }),
    portalAcceptInvite: build.mutation<
      PortalMe,
      { slug: string; token: string; password: string }
    >({
      query: ({ slug, token, password }) => ({
        url: `${portalBase(slug)}/invite/${encodeURIComponent(token)}`,
        method: 'POST',
        body: { password },
      }),
      invalidatesTags: ['Me', 'Data'],
    }),
    portalLogOut: build.mutation<unknown, string>({
      query: (slug) => ({ url: `${portalBase(slug)}/logout`, method: 'POST' }),
      invalidatesTags: ['Me', 'Data'],
    }),
    portalOverview: build.query<PortalOverview, string>({
      query: (slug) => `${portalBase(slug)}/overview`,
      providesTags: ['Data'],
    }),
    portalCampaigns: build.query<PortalCampaignsResult, string>({
      query: (slug) => `${portalBase(slug)}/campaigns`,
      providesTags: ['Data'],
    }),
    portalCampaign: build.query<
      PortalCampaignResult,
      { slug: string; campaignId: string; days: number }
    >({
      query: ({ slug, campaignId, days }) =>
        `${portalBase(slug)}/campaigns/${encodeURIComponent(campaignId)}?days=${days}`,
      providesTags: ['Data'],
    }),
    portalReplies: build.query<PortalRepliesResult, { slug: string; offset: number }>({
      query: ({ slug, offset }) => `${portalBase(slug)}/replies?limit=${REPLIES_PAGE}&offset=${offset}`,
      providesTags: ['Data'],
    }),
    portalReply: build.query<PortalReplyResult, { slug: string; conversationId: string }>({
      query: ({ slug, conversationId }) =>
        `${portalBase(slug)}/replies/${encodeURIComponent(conversationId)}`,
      providesTags: ['Data'],
    }),
  }),
})



export const {
  usePortalMeQuery,
  usePortalLogInMutation,
  usePortalAcceptInviteMutation,
  usePortalLogOutMutation,
  usePortalOverviewQuery,
  usePortalCampaignsQuery,
  usePortalCampaignQuery,
  usePortalRepliesQuery,
  usePortalReplyQuery,
} = portalApi
